// Strength-standards bands: interpret an estimated 1RM as a multiple of bodyweight and map it to
// a training level (Untrained→Elite). A rough, widely-used guide for the main barbell lifts + common
// variants; the in-app reference (screens/Standards.js) shows the table and cites the sources.
//
// BASE lifts carry male 1RM÷BW thresholds `t` = [Beginner, Intermediate, Advanced, Elite] (below
// the first = Untrained — ExRx's term; "Novice" was dropped because the popular sites we cite use
// it for the band ABOVE Beginner) and an `exclude` list so a variant is never mis-graded against
// them. Each base lift also carries `fScale`, the female-threshold multiplier — the sex gap is
// lift-dependent (upper-body ≈0.55–0.65, lower-body ≈0.75), so a flat scale would grade women's
// pressing too harshly. VARIANTS
// derive their own thresholds from a base lift via a documented average strength ratio (`from` +
// `factor`) — so a front squat is scored on front-squat numbers, never the back-squat ones.
// Dumbbell variants use the COMBINED weight of both dumbbells (the total you log) — so a DB lift is
// graded against ~twice one dumbbell. Only two-arm DB presses are recognised; single-arm variants are
// excluded (their reps are logged across both sides, so they can't be graded). Anything not matched
// here is simply not scored (returns null).
const LIFTS = [
  // ── Base barbell lifts ───────────────────────────────────────────────────────
  { key: "deadlift", name: "Deadlift", catalog: "Barbell Deadlift",
    match: ["deadlift"],
    exclude: ["romanian", "stiff", "rdl", "deficit", "rack", "trap bar", "trap-bar", "hex", "dumbbell", "single", "one arm", "one-arm", "snatch grip", "snatch-grip", "good morning", "jefferson", "suitcase"],
    t: [1.5, 2.0, 2.75, 3.25], fScale: 0.75 },
  { key: "squat", name: "Back Squat", catalog: "Barbell Full Squat",
    match: ["squat"],
    exclude: ["front", "split", "bulgarian", "hack", "goblet", "overhead", "zercher", "sissy", "box", "jefferson", "belt", "landmine", "single", "pistol", "cossack", "dumbbell", "smith", "kettlebell", "wall"],
    t: [1.25, 1.75, 2.5, 3.0], fScale: 0.75 },
  { key: "bench", name: "Bench Press", catalog: "Barbell Bench Press - Medium Grip",
    match: ["bench press"],
    exclude: ["incline", "decline", "dumbbell", "close grip", "close-grip", "floor", "smith", "machine", "spoto", "larsen", "reverse", "wide grip", "wide-grip", "board", "pin", "feet up", "guillotine"],
    t: [0.75, 1.25, 1.75, 2.0], fScale: 0.58 },
  { key: "row", name: "Bent-Over Row", catalog: "Bent Over Barbell Row",
    match: ["barbell row", "bent over row", "bent-over row", "pendlay row"],
    exclude: ["dumbbell", "one arm", "one-arm", "single", "smith", "machine", "seal", "cable", "t-bar", "t bar", "chest supported", "chest-supported", "incline", "landmine", "upright"],
    t: [0.8, 1.2, 1.6, 2.0], fScale: 0.65 },
  { key: "ohp", name: "Overhead Press", catalog: "Standing Military Press",
    match: ["overhead press", "military press", "shoulder press"],
    exclude: ["dumbbell", "machine", "smith", "arnold", "seated dumbbell", "landmine", "push press", "behind the neck", "behind-the-neck", "single", "one arm", "one-arm", "cable", "z press", "z-press"],
    t: [0.55, 0.8, 1.1, 1.4], fScale: 0.62 },

  // ── Variants — own standard, derived from a base lift via a documented strength ratio.
  //    DB lifts use the COMBINED weight of both dumbbells (the total you log). ──────
  { key: "front-squat", name: "Front Squat", catalog: "Front Barbell Squat",
    match: ["front squat", "front barbell squat"], from: "squat", factor: 0.85,
    note: "≈ 85% of back squat" },
  { key: "db-bench", name: "Dumbbell Bench Press", catalog: "Dumbbell Bench Press",
    match: ["dumbbell bench", "db bench"], from: "bench", factor: 0.75,
    exclude: ["incline", "decline", "single", "one arm", "one-arm"],
    note: "both dumbbells, ≈ 75% of barbell bench" },
  { key: "incline-db", name: "Incline Dumbbell Press", catalog: "Incline Dumbbell Press",
    match: ["incline dumbbell"], from: "bench", factor: 0.60,
    exclude: ["single", "one arm", "one-arm"],
    note: "both dumbbells, ≈ 60% of barbell bench (incline ×0.8, dumbbell ×0.75)" },
  { key: "db-ohp", name: "Dumbbell Shoulder Press", catalog: "Dumbbell Shoulder Press",
    match: ["dumbbell shoulder press", "seated dumbbell press", "dumbbell overhead", "dumbbell military"], from: "ohp", factor: 0.65,
    exclude: ["single", "one arm", "one-arm"],
    note: "both dumbbells, ≈ 65% of barbell overhead press" },
];

// Fill in each variant's thresholds from its base lift × factor (rounded to 0.01). Variants
// inherit the base lift's female scale — the sex gap tracks the movement pattern, not the implement.
for (const l of LIFTS) {
  if (l.from && !l.t) {
    const base = LIFTS.find((b) => b.key === l.from);
    l.t = base.t.map((x) => Math.round(x * l.factor * 100) / 100);
    l.fScale = base.fScale;
  }
}

const LEVELS = [
  { name: "Beginner", color: "var(--text-2)" },
  { name: "Intermediate", color: "var(--accent)" },
  { name: "Advanced", color: "var(--success)" },
  { name: "Elite", color: "var(--warn)" },
];
const FEMALE_SCALE = 0.72;

// The most specific recognised lift for an exercise name: a `match` term must hit and no `exclude`
// term may. Base lifts are listed first but exclude their own variants, so a variant resolves to
// its own entry; an unrecognised lift (machine, accessory, unilateral) returns null and isn't scored.
export function liftFor(name) {
  const n = (name || "").toLowerCase();
  return LIFTS.find((l) => l.match.some((m) => n.includes(m)) && !(l.exclude || []).some((e) => n.includes(e))) || null;
}

/**
 * Strength level for a lift given estimated 1RM ÷ bodyweight ratio and sex ('female' scales the
 * thresholds down by the lift's own factor). Returns { level, color } for a recognised lift, else
 * null. Below the Beginner threshold reads as "Untrained".
 */
export function strengthLevel(exerciseName, ratio, sex) {
  const lift = liftFor(exerciseName);
  if (!lift || ratio == null || !isFinite(ratio)) return null;
  const scale = sex === "female" ? (lift.fScale ?? FEMALE_SCALE) : 1;
  const thresholds = lift.t.map((x) => x * scale);
  let idx = -1;
  for (let i = 0; i < thresholds.length; i++) if (ratio >= thresholds[i]) idx = i;
  if (idx === -1) return { level: "Untrained", color: "var(--text-3)" };
  return { level: LEVELS[idx].name, color: LEVELS[idx].color };
}

// Band → ordinal, so several lifts can be averaged into one training level.
const LEVEL_NAMES = ["Untrained", "Beginner", "Intermediate", "Advanced", "Elite"];
const LEVEL_INDEX = { Untrained: 0, Beginner: 1, Intermediate: 2, Advanced: 3, Elite: 4 };

/**
 * Aggregate a lifter's overall training level from their main-lift records. For every recognised
 * lift among `records` (best-estimated-1RM rows: {exercise_name, estimated_1rm_kg}), classify
 * e1RM ÷ bodyweight into a band, then average the band ordinals and round. Returns { level, index,
 * n } where `n` is how many recognised lifts contributed, or null when bodyweight is unknown or no
 * recognised lift is present — so callers degrade gracefully rather than guessing.
 */
export function overallStrengthLevel(records, bodyweightKg, sex) {
  if (!bodyweightKg || bodyweightKg <= 0 || !Array.isArray(records)) return null;
  const indices = [];
  for (const r of records) {
    const e1rm = r && (r.estimated_1rm_kg ?? r.e1rm_kg);
    if (!e1rm || e1rm <= 0) continue;
    const lv = strengthLevel(r.exercise_name, e1rm / bodyweightKg, sex);
    if (lv && lv.level in LEVEL_INDEX) indices.push(LEVEL_INDEX[lv.level]);
  }
  if (!indices.length) return null;
  const index = Math.round(indices.reduce((a, b) => a + b, 0) / indices.length);
  return { level: LEVEL_NAMES[index], index, n: indices.length };
}

// ── Reference data for the in-app Strength Standards screen (screens/Standards.js) ──────
// Population/percentile guides — NOT clinical thresholds. Exposed so the reference can teach
// how a level is derived and link each lift to its real catalog entry.


// The five bands, with the PR-board colours + a one-line description. Order = Untrained → Elite.
export const STRENGTH_LEVELS = [
  { name: "Untrained", color: "var(--text-3)", blurb: "New to the lift — still grooving the movement; strength climbs quickly with consistent practice." },
  { name: "Beginner", color: "var(--text-2)", blurb: "A few months of training the lift, with basic proficiency." },
  { name: "Intermediate", color: "var(--accent)", blurb: "Consistent training over months to a couple of years; clearly stronger than an untrained adult." },
  { name: "Advanced", color: "var(--success)", blurb: "Years of dedicated training; strength well above the general population." },
  { name: "Elite", color: "var(--warn)", blurb: "Competition-calibre strength for the lift." },
];

// Documented bases for the bands, shown verbatim in the reference's "Sources" section.
export const STANDARDS_SOURCES = [
  { source: "ExRx.net — Strength Standards", finding: "Published barbell strength standards classifying lifts from untrained to elite by bodyweight and sex.", url: "https://exrx.net/Testing/WeightLifting/StrengthStandards" },
  { source: "StrengthLevel — Strength Standards", finding: "Relative-strength standards (beginner→elite) aggregated from a large public dataset of logged lifts, expressed as a multiple of bodyweight.", url: "https://strengthlevel.com/strength-standards" },
  { source: "Rippetoe & Kilgore — Practical Programming for Strength Training (3rd ed.)", finding: "Defines the untrained → novice → intermediate → advanced → elite classification by training experience and rate of adaptation.", url: "" },
  { source: "Jaric 2002, Sports Medicine 32(10):615", finding: "Reviews normalizing muscle strength to body size; supports comparing lifters by strength-to-bodyweight ratio rather than absolute load.", url: "https://pubmed.ncbi.nlm.nih.gov/12141882/" },
  { source: "Variant ratios — front squat ≈ 85% of back squat", finding: "Front-squat 1RM is typically ~80–85% of back-squat 1RM in lifters who train both.", url: "https://strengthlevel.com/strength-standards/front-squat" },
  { source: "Variant ratios — incline ≈ 80% of flat bench", finding: "In trained men, flat-bench 1RM runs ~22–29% above incline; incline ≈ 78–82% of flat.", url: "https://strengthlevel.com/strength-standards/incline-bench-press" },
  { source: "Variant ratios — dumbbell vs barbell", finding: "Dumbbell bench totals ~73–75% of barbell bench; dumbbell overhead press ~60–70% of barbell — graded here as the combined weight of both dumbbells.", url: "https://modules.scorenetwork.org/fitness_and_training/dumbbell_barbell_bench_ratio/index.html" },
  { source: "Nuzzo 2023, J Strength Cond Res 37(2):494 — sex differences in strength", finding: "The female–male strength gap is lift-dependent: women average roughly 50–60% of men's upper-body strength but 60–75% of lower-body strength — so female thresholds here scale per lift (pressing ≈0.58–0.62, row 0.65, squat/deadlift 0.75) rather than by one flat factor.", url: "https://pubmed.ncbi.nlm.nih.gov/36135029/" },
];

// Threshold table for the reference, scaled for the given sex (female → each lift's own
// factor: pressing ≈0.58–0.62, row 0.65, squat/deadlift 0.75). Each row's `t` is
// [Beginner, Intermediate, Advanced, Elite] as a multiple of bodyweight; `variant` rows carry a
// derivation `note` (dumbbell rows are graded as the combined weight of both dumbbells).
export function standardsTable(sex) {
  return LIFTS.map((l) => ({
    key: l.key,
    name: l.name,
    catalog: l.catalog,
    variant: !!l.from,
    note: l.note || null,
    t: l.t.map((x) => Math.round(x * (sex === "female" ? (l.fScale ?? FEMALE_SCALE) : 1) * 100) / 100),
  }));
}
